import { LevelTheme } from './types.js';
import type { Vec3 } from './types.js';

/**
 * Arc-length parameterized Catmull-Rom rail shared by client and server.
 * Both sides must sample identical world positions for hit detection to agree,
 * so this is plain math with no engine dependency.
 */
export class Rail {
  readonly length: number;

  private points: Vec3[];
  private samples: Vec3[] = [];
  private tangents: Vec3[] = [];
  private cumulative: number[] = [];
  private resolution: number;

  constructor(controlPoints: Vec3[], resolution = 2000) {
    if (controlPoints.length < 4) {
      throw new Error('Rail needs at least 4 control points');
    }
    this.points = controlPoints;
    this.resolution = resolution;

    let total = 0;
    let prev = this.evaluate(0);
    this.samples.push(prev);
    this.tangents.push(this.evaluateTangent(0));
    this.cumulative.push(0);

    for (let i = 1; i <= resolution; i++) {
      const u = i / resolution;
      const p = this.evaluate(u);
      total += Math.hypot(p.x - prev.x, p.y - prev.y, p.z - prev.z);
      this.samples.push(p);
      this.tangents.push(this.evaluateTangent(u));
      this.cumulative.push(total);
      prev = p;
    }

    this.length = total;
  }

  /** Position at arc-length fraction t in [0, 1]. */
  getPoint(t: number): Vec3 {
    const idx = this.arcIndex(t);
    return { ...this.samples[idx] };
  }

  /** Normalized forward direction at arc-length fraction t. */
  getTangent(t: number): Vec3 {
    const idx = this.arcIndex(t);
    return { ...this.tangents[idx] };
  }

  /**
   * World-space pose on the road: position offset sideways and up,
   * plus the forward/right basis vectors.
   */
  getPose(t: number, lateral: number, height = 0): { position: Vec3; forward: Vec3; right: Vec3 } {
    const idx = this.arcIndex(t);
    const p = this.samples[idx];
    const f = this.tangents[idx];
    // right = forward x up (up is +Y, track stays near-planar)
    const right = normalize({ x: -f.z, y: 0, z: f.x });
    return {
      position: {
        x: p.x + right.x * lateral,
        y: p.y + height,
        z: p.z + right.z * lateral,
      },
      forward: { ...f },
      right,
    };
  }

  /** Map arc-length fraction to the nearest uniform sample index. */
  private arcIndex(t: number): number {
    const target = Math.max(0, Math.min(1, t)) * this.length;
    // binary search cumulative lengths
    let lo = 0;
    let hi = this.cumulative.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (this.cumulative[mid] < target) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  /** Uniform-parameter Catmull-Rom evaluation (tension 0.5). */
  private evaluate(u: number): Vec3 {
    const { i, s } = this.segment(u);
    const [p0, p1, p2, p3] = this.controlQuad(i);

    const s2 = s * s;
    const s3 = s2 * s;
    const h1 = 2 * s3 - 3 * s2 + 1;
    const h2 = -2 * s3 + 3 * s2;
    const h3 = s3 - 2 * s2 + s;
    const h4 = s3 - s2;

    return {
      x: h1 * p1.x + h2 * p2.x + h3 * 0.5 * (p2.x - p0.x) + h4 * 0.5 * (p3.x - p1.x),
      y: h1 * p1.y + h2 * p2.y + h3 * 0.5 * (p2.y - p0.y) + h4 * 0.5 * (p3.y - p1.y),
      z: h1 * p1.z + h2 * p2.z + h3 * 0.5 * (p2.z - p0.z) + h4 * 0.5 * (p3.z - p1.z),
    };
  }

  private evaluateTangent(u: number): Vec3 {
    const { i, s } = this.segment(u);
    const [p0, p1, p2, p3] = this.controlQuad(i);

    const s2 = s * s;
    const d1 = 6 * s2 - 6 * s;
    const d2 = -6 * s2 + 6 * s;
    const d3 = 3 * s2 - 4 * s + 1;
    const d4 = 3 * s2 - 2 * s;

    return normalize({
      x: d1 * p1.x + d2 * p2.x + d3 * 0.5 * (p2.x - p0.x) + d4 * 0.5 * (p3.x - p1.x),
      y: d1 * p1.y + d2 * p2.y + d3 * 0.5 * (p2.y - p0.y) + d4 * 0.5 * (p3.y - p1.y),
      z: d1 * p1.z + d2 * p2.z + d3 * 0.5 * (p2.z - p0.z) + d4 * 0.5 * (p3.z - p1.z),
    });
  }

  private segment(u: number): { i: number; s: number } {
    const segments = this.points.length - 1;
    const clamped = Math.max(0, Math.min(1, u));
    const scaled = clamped * segments;
    const i = Math.min(Math.floor(scaled), segments - 1);
    return { i, s: scaled - i };
  }

  private controlQuad(i: number): [Vec3, Vec3, Vec3, Vec3] {
    const pts = this.points;
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    return [p0, p1, p2, p3];
  }
}

function normalize(v: Vec3): Vec3 {
  const len = Math.hypot(v.x, v.y, v.z) || 1;
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}

function p(x: number, z: number): Vec3 {
  return { x, y: 0, z };
}

/**
 * One authored rail per theme. All start and end on a straight (spawn arch /
 * boss arena) and are roughly the same length.
 */
const THEME_POINTS: Record<LevelTheme, Vec3[]> = {
  // Rolling highway: sweeping bends, one S-combo, long finale straight.
  [LevelTheme.Countryside]: [
    p(0, 0), p(0, -60), p(8, -130), p(22, -200), p(18, -280), p(-4, -350),
    p(-26, -420), p(-30, -500), p(-12, -570), p(14, -630), p(34, -700),
    p(30, -780), p(8, -850), p(-10, -920), p(-8, -1000), p(0, -1080),
    p(0, -1160), p(0, -1240),
  ],
  // Downtown grid: straights broken by hard lane-shift chicanes.
  [LevelTheme.City]: [
    p(0, 0), p(0, -80), p(0, -160), p(-16, -225), p(-16, -305), p(0, -370),
    p(16, -435), p(16, -515), p(0, -580), p(-16, -645), p(-16, -725),
    p(0, -790), p(0, -870), p(16, -935), p(16, -1015), p(0, -1080),
    p(0, -1160), p(0, -1240),
  ],
  // Underground tube: constant gentle serpentine, never a straight sightline.
  [LevelTheme.Tunnel]: [
    p(0, 0), p(0, -70), p(7, -140), p(13, -210), p(7, -280), p(-7, -350),
    p(-13, -420), p(-7, -490), p(7, -560), p(13, -630), p(7, -700),
    p(-7, -770), p(-13, -840), p(-7, -910), p(0, -980), p(0, -1060),
    p(0, -1140),
  ],
  // Open desert: huge fast sweepers between mesas.
  [LevelTheme.Desert]: [
    p(0, 0), p(0, -90), p(20, -180), p(45, -270), p(55, -370), p(40, -470),
    p(10, -550), p(-25, -630), p(-45, -730), p(-40, -830), p(-15, -910),
    p(10, -990), p(20, -1080), p(10, -1170), p(0, -1250), p(0, -1330),
  ],
  // Bayou causeway: tight technical winding through the murk.
  [LevelTheme.Swamp]: [
    p(0, 0), p(0, -60), p(10, -110), p(18, -165), p(8, -220), p(-10, -270),
    p(-20, -330), p(-10, -390), p(8, -440), p(18, -495), p(10, -550),
    p(-8, -605), p(-18, -660), p(-10, -715), p(6, -770), p(16, -830),
    p(6, -890), p(-6, -945), p(0, -1010), p(0, -1090), p(0, -1170),
  ],
};

/** Uniform stretch applied to every authored track (longer levels, gentler curves). */
// Long enough for a distinct one-minute pursuit before target contact, while
// preserving a substantial moving boss encounter in the back half.
const TRACK_SCALE = 2.25;

const THEME_ORDER: LevelTheme[] = [
  LevelTheme.Countryside,
  LevelTheme.City,
  LevelTheme.Tunnel,
  LevelTheme.Desert,
  LevelTheme.Swamp,
];

/** Levels cycle through the themes: 1=countryside, 2=city, ... 6=countryside. */
export function getLevelTheme(level: number): LevelTheme {
  return THEME_ORDER[(Math.max(1, level) - 1) % THEME_ORDER.length];
}

export function createLevelRail(level: number): Rail {
  const points = THEME_POINTS[getLevelTheme(level)].map((pt) => ({
    x: pt.x * TRACK_SCALE,
    y: pt.y,
    z: pt.z * TRACK_SCALE,
  }));
  return new Rail(points);
}
